<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-lg font-semibold dark:text-white">
        GBP Post Campaigns
      </h2>
      <div class="flex items-center gap-3">
        <div class="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
          <button
            @click="viewMode = 'list'"
            class="p-1.5 rounded-md transition-colors"
            :class="viewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'"
            title="List view">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
          </button>
          <button
            @click="viewMode = 'calendar'"
            class="p-1.5 rounded-md transition-colors"
            :class="viewMode === 'calendar' ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'"
            title="Calendar view">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          </button>
        </div>
        <button @click="openNewCampaignForm" class="btn btn-primary btn-sm">
          New Campaign
        </button>
      </div>
    </div>

    <template v-if="viewMode === 'calendar'">
      <GbpCalendar :location-id="locationId" />
    </template>

    <template v-else>
      <GbpCampaignShow
        v-if="selectedCampaign"
        :campaign="selectedCampaign"
        @back="selectedCampaign = null"
        @updated="fetchCampaigns" />

      <div v-if="!selectedCampaign">
        <div v-if="campaigns.length === 0" class="text-gray-500 dark:text-gray-400 text-center py-12">
          <p class="text-base mb-2">
            No campaigns yet
          </p>
          <p class="text-sm">
            Create a campaign to generate GBP posts with AI.
          </p>
        </div>
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date Range
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Posts
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th class="px-4 py-3" />
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="c in campaigns"
                :key="c.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer"
                @click="selectedCampaign = c">
                <td class="px-4 py-3 text-sm font-medium text-primary-500 dark:text-primary-400">
                  {{ c.name }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {{ c.date_range_start }} &ndash; {{ c.date_range_end }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                  {{ c.posts_count }}
                </td>
                <td class="px-4 py-3">
                  <span :class="statusClass(c.status)" class="px-2 py-0.5 rounded-full text-xs font-medium">{{ c.status }}</span>
                </td>
                <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {{ formatDate(c.created_at) }}
                </td>
                <td class="px-4 py-3 text-right">
                  <button @click.stop="deleteCampaign(c)" class="text-red-500 text-xs hover:underline">
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <UiSlidePanel ref="slidePanel" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getGbpCampaigns, destroyGbpCampaign } from '~/javascript/api/gbp_campaigns'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'
import GbpCampaignForm from './form.vue'
import GbpCampaignShow from './show.vue'
import GbpCalendar from '~/javascript/components/gbp_posts/calendar/calendar.vue'

const props = defineProps({
  locationId: { type: String, default: '' },
  defaultKeywords: { type: Array, default: () => []}
})

const viewMode = ref('list')
const campaigns = ref([])
const selectedCampaign = ref(null)
const slidePanel = ref(null)

async function fetchCampaigns() {
  const { data } = await getGbpCampaigns(props.locationId)
  campaigns.value = data
}

function onCampaignCreated(campaign) {
  slidePanel.value.close()
  selectedCampaign.value = campaign
  fetchCampaigns()
}

function openNewCampaignForm() {
  slidePanel.value.open({
    component: GbpCampaignForm,
    props: { locationId: props.locationId, defaultKeywords: props.defaultKeywords },
    events: { created: onCampaignCreated }
  })
}

async function deleteCampaign(campaign) {
  // eslint-disable-next-line no-restricted-globals, no-alert
  if (!confirm('Delete this campaign and all its posts?')) return
  await destroyGbpCampaign(campaign.id)
  fetchCampaigns()
}

function statusClass(status) {
  const classes = {
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    processing: 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200',
    completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  }
  return classes[status] || ''
}

function formatDate(d) {
  return new Date(d).toLocaleDateString()
}

onMounted(fetchCampaigns)
</script>
